import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from 'react-router-dom';
import {
    App as AntdApp,
    Card,
    DatePicker,
    Button,
    Spin,
    Select,
} from "antd";
import {
	DownloadOutlined,
	DownOutlined,
	ReloadOutlined,
	RightOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import * as XLSX from "xlsx";
import styled from "styled-components";
import { RangeValue } from "rc-picker/lib/interface";
import { authAxios, getEmailFromSessionStorage } from "../utils/session_utils";
import {
	analyticsValidationService,
	AnalyticsValidationRecord,
} from "../../services/analyticsValidationService";

import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Filler,
	Legend,
	ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { RcFile, UploadFile, UploadProps } from "antd/es/upload";
import { useOutletContext } from "react-router-dom";
import { isAxiosError } from "axios";
import AnalyticsUploadModal from "./AnalyticsUploadModal";
import AnalyticsFilterBar, { FilterDimension } from "./AnalyticsFilterBar";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Filler,
	Legend
);

export const options: ChartOptions<"line"> = {
	responsive: true,
	maintainAspectRatio: false,
	scales: {
		yLeft: {
			type: "linear" as const,
			position: "left" as const,
			beginAtZero: true,
			title: {
				display: true,
				text: "Hours (HOV)",
			},
		},
	},
	plugins: {
		legend: {
			position: "top",
		},
		tooltip: {
			mode: "index",
			intersect: false,
			callbacks: {
				title: tooltipItems => {
					// Show date from label
					return tooltipItems[0].label || "";
				},
				label: tooltipItem => {
					const value = tooltipItem.formattedValue;
					return `${tooltipItem.dataset.label}: ${value}`;
				},
			},
		},
	},
	interaction: {
		mode: "index",
		intersect: false,
	},
};

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

interface DataRow {
	"date": string; // YYYY-MM-DD
	"hours": number;
	"impressions": number;
	"impression/hour": number;
}

interface ExportDataRow extends DataRow {
	platform: string;
	channel?: string;
	territory?: string;
	impressionPerHour: number;
}

const StyledCard = styled(Card)`
	&.ant-card {
		// max-height: calc(100vh - 64px);
		// overflow: hidden;
	}
	.ant-card-head-wrapper {
		flex-wrap: wrap;
		gap: 8px;
	}
	.ant-card-extra {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-left: auto;
	}
	.ant-card-body {
		.recharts-responsive-container,
		.ant-table-wrapper {
			margin-top: 30px;
		}
	}
`;

const ToolbarField = styled.div`
	display: inline-flex;
	align-items: center;
	height: 32px;
	padding: 0 4px 0 11px;
	border: 1px solid #d9d9d9;
	border-radius: 6px;

	.toolbar-field-label {
		font-size: 13px;
		color: rgba(0, 0, 0, 0.45);
		white-space: nowrap;
	}

	.ant-picker {
		padding-top: 0;
		padding-bottom: 0;
	}
`;

type SliceDimension = "none" | "platform" | "channel" | "territory";
type SliceMetric = "impressions" | "hours" | "impressionsPerHour";

const sliceDimensionLabel: Record<SliceDimension, string> = {
	none: "None",
	platform: "Platform",
	channel: "Channel",
	territory: "Territory",
};

const sliceMetricLabel: Record<SliceMetric, string> = {
	impressions: "Impressions",
	hours: "Hours",
	impressionsPerHour: "Impressions/Hour",
};

const sliceOptions = (["none", "platform", "channel", "territory"] as SliceDimension[]).map(dim => ({
	value: dim,
	label: sliceDimensionLabel[dim],
}));

const isAllSelected = (selected: string[] | undefined, records: AnalyticsValidationRecord[]) =>
	!selected?.length ||
	(records.length > 0 && records.every(record => selected.includes(record.value)));

const toRequestFilter = (selected: string[] | undefined, records: AnalyticsValidationRecord[]) =>
	isAllSelected(selected, records) ? [] : selected ?? [];

const AnalyticsContent: React.FC = () => {
	const { message } = AntdApp.useApp();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [removeDuplicates, setRemoveDuplicates] = useState(true);
	const [exportData, setExportData] = useState<ExportDataRow[]>([]);
	const [platforms, setPlatforms] = useState<AnalyticsValidationRecord[]>([]);
	const [channels, setChannels] = useState<AnalyticsValidationRecord[]>([]);
	const [territories, setTerritories] = useState<AnalyticsValidationRecord[]>([]);
	const { user }: any = useOutletContext();
	const [filters, setFilters] = useState({
		platforms: [] as string[],
		channels: [] as string[],
		territories: [] as string[],
		dateRange: [dayjs().subtract(30, "day"), dayjs()] as [Dayjs, Dayjs],
	});
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [file, setFile] = useState<any>(null);
	// Filters are always expanded on page load / reload — this is intentionally not persisted.
	const [filtersExpanded, setFiltersExpanded] = useState(true);

	const [sliceBy, setSliceBy] = useState<SliceDimension>("none");
	const [sliceMetric, setSliceMetric] = useState<SliceMetric>("hours");



	const handleFileRemove = () => {
		setFile(null);
		setFileList([]);
		return true;
	};

	const beforeUpload = (file: any) => {
		setFileList([file]);
		setFile(file);
		return false;
	};

	const uploadProps: UploadProps = {
		accept: ".csv",
		onRemove: handleFileRemove,
		beforeUpload: beforeUpload,
		fileList,
	};


	const platformsObj = useMemo(() => {
		const tempObj: {[key: string]: string} = {};
		platforms.forEach(platform => tempObj[platform.value] = platform.ui_label)
		return tempObj
	}, [platforms])

	const exportToCSV = () => {
		const headers = ["Date", "Platform", "Channel", "Territory", "Hours", "Impressions", "Impressions/Hours"]
		const finalExportData = exportData
			.filter(item => {
				if (!filters.dateRange) return true;
				const itemDate = dayjs(item.date, "YYYY-MM-DD");
				return itemDate.isBetween(filters.dateRange[0].startOf("day"), filters.dateRange[1].endOf("day"), null, "[]");
			})
			.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
			.map(({date, channel, territory, platform, hours, impressions, impressionPerHour}) => [date, platformsObj[platform] ?? platform, channel, territory, hours, impressions, impressionPerHour]);


		const worksheetData = [headers, ...finalExportData];

		const ws = XLSX.utils.aoa_to_sheet(worksheetData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Analytics");
		XLSX.writeFile(wb, "analytics.csv");
	};

	/**
	 * Fetches validation data from the AnalyticsValidationService and separates
	 * platforms, channels, and territories based on the 'key' field in the validation records.
	 * Sets default values if no validation data is available or if an error occurs.
	 */
	const getValidationData = async () => {
		try {
			const validationRecords = await analyticsValidationService.getAllValidations();

			// Separate platforms, channels, and territories based on the 'key' field
			const platformRecords = validationRecords.filter(record => record.key === "Platform");
			const channelRecords = validationRecords.filter(record => record.key === "Channel");
			const territoryRecords = validationRecords.filter(record => record.key === "Territory");

			// Extract unique values
			const uniquePlatforms = platformRecords.sort((a, b) => a.value.localeCompare(b.value));
			const uniqueChannels = channelRecords.sort((a, b) => a.value.localeCompare(b.value));
			const uniqueTerritories = territoryRecords.sort((a, b) => a.value.localeCompare(b.value));

			setPlatforms(uniquePlatforms);
			setChannels(uniqueChannels);
			setTerritories(uniqueTerritories);
		} catch (error) {
			console.error("Validation data fetch error:", error);
			message.error("Unable to load validation data.");
		}
	};

	const getAnalyticsData = async (inputFilters?: typeof filters) => {
		try {
			const activeFilters = inputFilters ?? filters;
			if (!activeFilters) {
				setExportData([]);
				message.info("No Filters Selected.");
				return;
			}
			const {
				platforms: selectedPlatforms,
				channels: selectedChannels,
				territories: selectedTerritories,
				dateRange,
			} = activeFilters as any;
			if (!dateRange?.[0] || !dateRange?.[1]) {
				setExportData([]);
				message.info("Please select a date range.");
				return;
			}

			setLoading(true);
			const requestBody: any = {
				start_date: dateRange[0].format("YYYY-MM-DD"),
				end_date: dateRange[1].format("YYYY-MM-DD"),
				platforms: toRequestFilter(selectedPlatforms, platforms),
				channels: toRequestFilter(selectedChannels, channels),
				territories: toRequestFilter(selectedTerritories, territories),
			};

			const config = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/zype/analytics`,
				headers: {
					"Content-Type": "application/json",
				},
				data: requestBody,
			};

			const response = await authAxios(config);
			if (response?.data) {
				setExportData(response.data.exportData)
				message.success("Analytics data loaded successfully.");
			} else {
				message.warning("No data received from analytics endpoint.");
			}
		} catch (error) {
			console.error("Analytics data fetch error:", error);
			message.error("Unable to load analytics data. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const chartData = useMemo(() => {
		// Build datasets from exportData for all modes (including none)
		const dateSet = new Set<string>();
		const filteredExport = exportData.filter(item => {
			if (!filters.dateRange) return true;
			const itemDate = dayjs(item.date, "YYYY-MM-DD");
			return itemDate.isBetween(filters.dateRange[0].startOf("day"), filters.dateRange[1].endOf("day"), null, "[]");
		});
		// console.log("🚀 ~ Analytics ~ filteredExport:", filteredExport)
		filteredExport.forEach(r => dateSet.add(r.date));
		const labels = Array.from(dateSet).sort((a, b) => dayjs(a).unix() - dayjs(b).unix());
		// console.log("🚀 ~ Analytics ~ dateSet:", dateSet)

		// Roll up by date and group (slice)
		type Roll = { impressions: number; hours: number };
		const rollup: Record<string, Record<string, Roll>> = {};
		const getGroupKey = (row: ExportDataRow) => {
			if (sliceBy === "platform") return row.platform?.toUpperCase() || "Unknown";
			if (sliceBy === "channel") return row.channel?.toUpperCase() || "Unknown";
			if (sliceBy === "territory") return row.territory?.toUpperCase() || "Unknown";
			return "All";
		};
		for (const row of filteredExport) {
			const date = row.date;
			const group = getGroupKey(row);
			if (!rollup[date]) rollup[date] = {};
			if (!rollup[date][group]) rollup[date][group] = { impressions: 0, hours: 0 };
			rollup[date][group].impressions += row.impressions;
			rollup[date][group].hours += row.hours;
		}
		// console.table(rollup);

		// If no slicing, return a single aggregate series: Hours (HOV)
		if (sliceBy === "none") {
			const green = "rgba(75, 192, 192, 1)";
			const greenBg = "rgba(75, 192, 192, 0.2)";

			const hoursSeries = labels.map(date => {
				const totals = Object.values(rollup[date] || {}).reduce((acc, r) => ({
					impressions: acc.impressions + r.impressions,
					hours: acc.hours + r.hours,
				}), { impressions: 0, hours: 0 });
				return Number((totals.hours).toFixed(2));
			});

			return {
				labels,
				datasets: [
					{
						label: "Hours",
						data: hoursSeries,
						borderColor: green,
						backgroundColor: greenBg,
						fill: true,
						tension: 0.3,
						yAxisID: "yLeft" as const,
					},
				],
			};
		}

		// Build datasets per group
		const groups = new Set<string>();
		labels.forEach(d => Object.keys(rollup[d] || {}).forEach(g => groups.add(g)));
		const palette = [
			"#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6",
			"#06b6d4", "#ec4899", "#84cc16", "#f97316", "#22c55e",
		];
		// console.log("🚀 ~ Analytics ~ groups:", groups)
		const datasets = Array.from(groups).map((group, idx) => {
			const color = palette[idx % palette.length];
			const data = labels.map(date => {
				const r = rollup[date]?.[group];
				if (!r) return 0;
				if (sliceMetric === "hours") {
					return Number((r.hours).toFixed(2));
				}
				if (sliceMetric === "impressions") {
					return Number((r.impressions).toFixed(2));
				}
				if (r.hours === 0) return 0;
					return Number((r.impressions / r.hours).toFixed(2));

			});
			const label = `${sliceDimensionLabel[sliceBy as SliceDimension]}: ${group} (${sliceMetricLabel[sliceMetric]})`;
			return {
				label,
				data,
				borderColor: color,
				backgroundColor: `${color}33`,
				fill: false,
				tension: 0.3,
				yAxisID: "yLeft" as const,
			};
		});

		return { labels, datasets };
	}, [sliceBy, sliceMetric, exportData, filters.dateRange]);

	useEffect(() => {
		getValidationData();
	}, []);


	const onAnalyticsFileUpload = async (file: UploadFile | RcFile) => {
		try {
			if (!file) {
				return message.error("No file selected");
			}
			const fileObj = "originFileObj" in file ? file.originFileObj : file;
			if (!fileObj) {
				message.error("Invalid file object");
				return;
			}
			setIsUploading(true);
			const fileExtension = file.name?.split(".").pop()?.toLowerCase();
			if (!fileExtension) {
				message.error("Invalid file extension");
				return;
			}

			if (fileExtension !== "csv") {
				message.error("Unsupported file type");
				return;
			}
			const formData = new FormData();
			formData.append("file", file as RcFile);
			formData.append("name", user.name);
			formData.append("remove_duplicates", removeDuplicates.toString());
			formData.append("email", getEmailFromSessionStorage() as string);
			const config = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/import/viewership-analytics`,
				data: formData,
			};
			const response = await authAxios(config);
			message.success(response.data.message);
			setIsUploadModalOpen(false);
		} catch (error) {
			console.error("error in onAnalyticsFileUpload", error);
			if (isAxiosError(error)) {
				const msg = error?.response?.data?.message;
				message.error(msg ?? "Something went wrong");
			} else {
				message.error("Something went wrong");
			}
		} finally {
			setIsUploading(false);
		}
	};

	const handleUploadModalOpen = () => {
		setIsUploadModalOpen(true);
		handleFileRemove();
	};

	const handleUploadModalClose = () => {
		setIsUploadModalOpen(false);
		setRemoveDuplicates(true);
	};
	const lastHandledRef = useRef<string | null>(null);

	useEffect(() => {
		const filterQueryParam = searchParams.get("filter");
		if (filterQueryParam) {
			if (lastHandledRef.current === filterQueryParam) return;

			const queryFilters = JSON.parse(filterQueryParam);
			lastHandledRef.current = filterQueryParam;
			// convert dateRange strings to Dayjs
			const nextFilters = { ...filters, ...queryFilters } as any;
			if (queryFilters.dateRange && Array.isArray(queryFilters.dateRange)) {
				nextFilters.dateRange = [dayjs(queryFilters.dateRange[0]), dayjs(queryFilters.dateRange[1])] as [Dayjs, Dayjs];
			}
			// restore slice settings
			if (queryFilters.sliceBy) {
				setSliceBy(queryFilters.sliceBy);
			}
			if (queryFilters.sliceMetric) {
				setSliceMetric(queryFilters.sliceMetric);
			}
			setFilters(nextFilters);
			getAnalyticsData(nextFilters);
		} else {
			if (lastHandledRef.current === "__defaults__") return;
			lastHandledRef.current = "__defaults__";

			const defaultFilters = {
				platforms: [] as string[],
				channels: [] as string[],
				territories: [] as string[],
				dateRange: [dayjs().subtract(30, "day"), dayjs()] as [Dayjs, Dayjs],
			};
			setSliceBy("none");
			setSliceMetric("hours");
			setFilters(defaultFilters);
			getAnalyticsData(defaultFilters);
		}
	}, [searchParams, platforms, channels, territories])


	/**
	 * Writes the given filter/slice state to the URL. The `searchParams` effect above
	 * picks that up and refetches, so this is the single "apply" path for the page.
	 */
	const applyFilters = (
		nextFilters: typeof filters,
		nextSlice?: { sliceBy?: SliceDimension; sliceMetric?: SliceMetric }
	) => {
		const query = {
			platforms: nextFilters.platforms || [],
			channels: nextFilters.channels || [],
			territories: nextFilters.territories || [],
			dateRange:
				nextFilters.dateRange && nextFilters.dateRange.length === 2
					? [
							nextFilters.dateRange[0].format("YYYY-MM-DD"),
							nextFilters.dateRange[1].format("YYYY-MM-DD"),
					  ]
					: undefined,
			sliceBy: nextSlice?.sliceBy ?? sliceBy,
			sliceMetric: nextSlice?.sliceMetric ?? sliceMetric,
		} as any;
		setSearchParams({ filter: JSON.stringify(query) });
	};

	// Chips and the add-popover apply straight away — there is no Apply step any more.
	const handleFilterChange = (dimension: FilterDimension, values: string[]) => {
		const nextFilters = { ...filters, [dimension]: values };
		setFilters(nextFilters);
		applyFilters(nextFilters);
	};

	const handleRangeChange = (dates: RangeValue<Dayjs>) => {
		if (dates && dates[0] && dates[1]) {
			const nextFilters = { ...filters, dateRange: [dates[0], dates[1]] as [Dayjs, Dayjs] };
			setFilters(nextFilters);
			applyFilters(nextFilters);
		}
	};

	const handleSliceChange = (dimension: SliceDimension) => {
		setSliceBy(dimension);
		applyFilters(filters, { sliceBy: dimension });
	};

	return (
		<Spin spinning={loading}>
			<StyledCard
				title="Impressions Analytics"
				extra={
					<>
						<ToolbarField>
							<span className="toolbar-field-label">Date Range</span>
							<RangePicker
								variant="borderless"
								allowClear={false}
								format="MMM D, YYYY"
								value={filters.dateRange}
								onChange={handleRangeChange}
							/>
						</ToolbarField>
						<ToolbarField>
							<span className="toolbar-field-label">Slice</span>
							<Select
								variant="borderless"
								value={sliceBy}
								options={sliceOptions}
								onChange={handleSliceChange}
								style={{ minWidth: 110 }}
							/>
						</ToolbarField>
						<Button
							icon={filtersExpanded ? <DownOutlined /> : <RightOutlined />}
							onClick={() => setFiltersExpanded(!filtersExpanded)}
						>
							{filtersExpanded ? "Collapse Filters" : "Expand Filters"}
						</Button>
						<Button loading={loading} icon={<ReloadOutlined />} onClick={() => getAnalyticsData()}>
							Refresh Data
						</Button>
						<Button icon={<UploadOutlined />} onClick={handleUploadModalOpen}>
							Import Viewership
						</Button>
						<Button type="primary" icon={<DownloadOutlined />} onClick={exportToCSV}>
							Export CSV
						</Button>
					</>
				}
			>
				<AnalyticsFilterBar
					platforms={platforms}
					channels={channels}
					territories={territories}
					selection={{
						platforms: filters.platforms,
						channels: filters.channels,
						territories: filters.territories,
					}}
					onChange={handleFilterChange}
					expanded={filtersExpanded}
					onExpand={() => setFiltersExpanded(true)}
				/>

				<div style={{ width: "100%" }}>
					<Line options={options} data={chartData} height={320} />
				</div>
			</StyledCard>
			<AnalyticsUploadModal
				handleUpload={async () => {
					await onAnalyticsFileUpload(file);
				}}
				uploadProps={uploadProps}
				removeDuplicates={removeDuplicates}
				setRemoveDuplicates={e => setRemoveDuplicates(e.target.checked)}
				isUploadModalOpen={isUploadModalOpen}
				handleUploadModalClose={handleUploadModalClose}
				isUploading={isUploading}
			/>
		</Spin>
	);
};

const Analytics: React.FC = () => (
	<AntdApp component={false}>
		<AnalyticsContent />
	</AntdApp>
);

export default Analytics;
